import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  forwardRef,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CampaignStatus } from 'app/campaign/campaign.model';
import { WidgetSimpleInfo } from 'app/campaign/widgets.model';

export type WidgetSelectorIdSource = 'widget' | 'campaign';

type WidgetSelectorSortField = 'lastUpdated' | 'count';
type WidgetSelectorStatusTone = 'live' | 'scheduled' | 'stopped' | 'ended' | 'draft' | 'none';

interface WidgetSelectorRow {
  id: number;
  displayLabel: string;
  subLabel: string | null;
  searchText: string;
  statusTone: WidgetSelectorStatusTone;
  statusLabelKey: string | null;
  thumbnailUrl: string | null;
  lastUpdated: number;
  openCount: number;
}

interface WidgetSelectorItem extends WidgetSelectorRow {
  isSelected: boolean;
}

@Component({
  selector: 'app-widget-selector-menu',
  templateUrl: './widget-selector-menu.component.html',
  styleUrls: ['./widget-selector-menu.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => WidgetSelectorMenuComponent), multi: true }],
})
export class WidgetSelectorMenuComponent implements ControlValueAccessor, AfterViewChecked {
  @Input() set items(value: readonly WidgetSimpleInfo[]) {
    this._items.set(value ?? []);
  }

  /**
   * Whether a row (and the emitted value) is one widget ('widget', the default) or a whole
   * campaign ('campaign'). Campaign mode de-duplicates to one row per campaignId and emits
   * the campaignId.
   */
  @Input() set idSource(value: WidgetSelectorIdSource) {
    this._idSource.set(value ?? 'widget');
  }

  @Input() multiple = false;

  @Input() set counts(value: Map<number, number> | undefined | null) {
    this._counts.set(value ?? null);
  }

  @Input() showThumbnail = true;

  @Input() countLabel = '';

  @Input() placeholder = '';

  @Input() icon = '';

  @Input() set initialOnlyLive(value: boolean) {
    this.onlyLive.set(value);
  }

  @Input() set value(val: number[] | number | null) {
    this.writeValue(val);
  }

  @Output() valueChange = new EventEmitter<number[] | number | null>();

  @ViewChild('searchInput') private readonly searchInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('trigger') private readonly triggerRef: ElementRef<HTMLElement>;
  @ViewChildren('optionEl') private readonly optionEls: QueryList<ElementRef<HTMLElement>>;

  protected panelTop = 0;
  protected panelLeft = 0;
  protected panelWidth = 0;

  private readonly _items = signal<readonly WidgetSimpleInfo[]>([]);
  private readonly _counts = signal<Map<number, number> | null>(null);
  private readonly _idSource = signal<WidgetSelectorIdSource>('widget');

  protected readonly isOpen = signal(false);
  protected readonly searchQuery = signal('');
  protected readonly sortField = signal<WidgetSelectorSortField>('lastUpdated');
  // Last-updated sorting defaults to newest first.
  protected readonly sortAsc = signal(false);
  protected readonly selectedIds = signal<Set<number>>(new Set());
  protected readonly onlyLive = signal(false);
  // Row that holds the roving tabindex (keyboard focus).
  protected readonly activeIndex = signal(0);
  // Thumbnails that failed to load fall back to the placeholder instead of a broken image.
  protected readonly failedThumbnails = signal<ReadonlySet<string>>(new Set());
  protected isDisabled = false;

  private shouldFocusSearch = false;

  protected readonly hasCounts = computed(() => (this._counts()?.size ?? 0) > 0);

  private readonly normalizedItems = computed<WidgetSelectorRow[]>(() => {
    const items = this._items();
    const counts = this._counts();
    if (this._idSource() === 'campaign') {
      return this.normalizeByCampaign(items);
    }
    return items.filter((w) => w.id != null).map((w) => this.toWidgetRow(w, counts));
  });

  protected readonly singleSelectedLabel = computed<string | null>(() => {
    const ids = this.selectedIds();
    if (ids.size !== 1) return null;
    const id = [...ids][0];
    const item = this.normalizedItems().find((w) => w.id === id);
    return item ? item.displayLabel : String(id);
  });

  protected readonly multiSelectedCount = computed(() => this.selectedIds().size);

  protected readonly filteredItems = computed((): WidgetSelectorItem[] => {
    const query = this.searchQuery().toLowerCase().trim();
    const field = this.sortField();
    const hasCounts = this.hasCounts();
    const direction = this.sortAsc() ? 1 : -1;
    const selectedIds = this.selectedIds();

    let list = this.normalizedItems();
    if (this.onlyLive()) {
      list = list.filter((w) => w.statusTone === 'live');
    }
    if (query) {
      list = list.filter((w) => w.searchText.includes(query));
    }

    const sorted = [...list].sort((a, b) => {
      if (field === 'count' && hasCounts) {
        return (a.openCount - b.openCount) * direction;
      }
      if (a.lastUpdated !== b.lastUpdated) {
        return (a.lastUpdated - b.lastUpdated) * direction;
      }
      return a.displayLabel.localeCompare(b.displayLabel);
    });

    return sorted.map((w) => ({ ...w, isSelected: selectedIds.has(w.id) }));
  });

  private onChange: (value: number[] | number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private toWidgetRow(w: WidgetSimpleInfo, counts: Map<number, number> | null): WidgetSelectorRow {
    const id = w.id!;
    const displayLabel = w.campaignName ?? w.name ?? String(id);
    const subLabel = w.name && w.campaignName && w.name !== w.campaignName ? w.name : null;
    const status = this.statusInfo(w.campaignStatus ?? null);
    return {
      id,
      displayLabel,
      subLabel,
      searchText: `${displayLabel} ${w.name ?? ''}`.toLowerCase(),
      statusTone: status.tone,
      statusLabelKey: status.labelKey,
      thumbnailUrl: w.thumbnail ?? null,
      lastUpdated: this.toEpoch(w.lastUpdated),
      openCount: counts?.get(id) ?? 0,
    };
  }

  private normalizeByCampaign(items: readonly WidgetSimpleInfo[]): WidgetSelectorRow[] {
    const byCampaign = new Map<number, WidgetSelectorRow>();
    for (const w of items) {
      if (w.campaignId == null) {
        continue;
      }
      const updated = this.toEpoch(w.lastUpdated);
      const existing = byCampaign.get(w.campaignId);
      if (!existing) {
        const displayLabel = w.campaignName ?? w.name ?? String(w.campaignId);
        const status = this.statusInfo(w.campaignStatus ?? null);
        byCampaign.set(w.campaignId, {
          id: w.campaignId,
          displayLabel,
          subLabel: null,
          searchText: displayLabel.toLowerCase(),
          statusTone: status.tone,
          statusLabelKey: status.labelKey,
          thumbnailUrl: w.thumbnail ?? null,
          lastUpdated: updated,
          openCount: 0,
        });
      } else {
        if (updated > existing.lastUpdated) {
          existing.lastUpdated = updated;
        }
        if (!existing.thumbnailUrl && w.thumbnail) {
          existing.thumbnailUrl = w.thumbnail;
        }
      }
    }
    return [...byCampaign.values()];
  }

  /** Dot + label, never color alone; labels reuse the app-wide campaign.status-* translations. */
  private statusInfo(status: CampaignStatus | null): { tone: WidgetSelectorStatusTone; labelKey: string | null } {
    switch (status) {
      case CampaignStatus.Published:
        return { tone: 'live', labelKey: 'campaign.status-Published-2' };
      case CampaignStatus.Scheduled:
        return { tone: 'scheduled', labelKey: 'campaign.status-Scheduled' };
      case CampaignStatus.Unpublished:
        return { tone: 'stopped', labelKey: 'campaign.status-Unpublished' };
      case CampaignStatus.Finished:
        return { tone: 'ended', labelKey: 'campaign.status-Finished' };
      case CampaignStatus.Draft:
        return { tone: 'draft', labelKey: 'campaign.status-Draft' };
      default:
        return { tone: 'none', labelKey: null };
    }
  }

  private toEpoch(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  writeValue(value: number[] | number | null): void {
    if (this.multiple) {
      const arr = Array.isArray(value) ? value : value != null ? [value as number] : [];
      this.selectedIds.set(new Set(arr));
    } else {
      this.selectedIds.set(value != null ? new Set([value as number]) : new Set());
    }
  }

  registerOnChange(fn: (value: number[] | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected toggleOpen(): void {
    if (this.isDisabled) return;
    const opening = !this.isOpen();
    if (opening) {
      this.updatePanelPosition();
      this.searchQuery.set('');
      this.shouldFocusSearch = true;
      // Make the selected row (or the first row) the keyboard entry point.
      const items = this.filteredItems();
      const selected = items.findIndex((i) => i.isSelected);
      this.activeIndex.set(selected >= 0 ? selected : 0);
    }
    this.isOpen.set(opening);
    this.onTouched();
  }

  private updatePanelPosition(): void {
    if (!this.triggerRef) return;
    const rect = this.triggerRef.nativeElement.getBoundingClientRect();
    this.panelTop = rect.bottom + 4;
    this.panelLeft = rect.left;
    this.panelWidth = Math.max(rect.width, 320);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected selectItem(item: WidgetSelectorItem): void {
    if (this.multiple) {
      const next = new Set(this.selectedIds());
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      this.selectedIds.set(next);
      const val = [...next];
      this.onChange(val);
      this.valueChange.emit(val);
    } else {
      this.selectedIds.set(new Set([item.id]));
      this.onChange(item.id);
      this.valueChange.emit(item.id);
      this.close();
    }
  }

  protected onThumbnailError(url: string): void {
    this.failedThumbnails.update((failed) => new Set(failed).add(url));
  }

  protected clearSelection(): void {
    if (this.selectedIds().size === 0) return;
    this.selectedIds.set(new Set());
    const val = this.multiple ? [] : null;
    this.onChange(val);
    this.valueChange.emit(val);
  }

  protected toggleSort(field: WidgetSelectorSortField): void {
    if (this.sortField() === field) {
      this.sortAsc.update((v) => !v);
    } else {
      this.sortField.set(field);
      // Both sorts default to "most first": newest updates / highest counts.
      this.sortAsc.set(false);
    }
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    // The list contents changed; reset the keyboard entry point to the top.
    this.activeIndex.set(0);
  }

  protected onSearchArrowDown(event: Event): void {
    event.preventDefault();
    this.focusOption(0);
  }

  protected onOptionKeydown(event: KeyboardEvent, item: WidgetSelectorItem, index: number): void {
    const count = this.filteredItems().length;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectItem(item);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusOption(Math.min(index + 1, count - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusOption(Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusOption(count - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.triggerRef?.nativeElement.focus();
        break;
    }
  }

  private focusOption(index: number): void {
    if (index < 0) return;
    this.activeIndex.set(index);
    this.optionEls?.get(index)?.nativeElement.focus();
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleOpen();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusSearch && this.searchInputRef) {
      this.searchInputRef.nativeElement.focus();
      this.shouldFocusSearch = false;
    }
  }
}
